import React from 'react';
import TimeAgoTicker from '../TimeAgoTicker';
import { observer } from 'mobx-react';
import { useParams } from 'react-router-dom';
import { RSIcon } from 'reactsymbols-kit';
import { useStores } from '../../hooks/useStores';

/**
 * The autosave state of the project (mirrors the non-exported `SaveState` in
 * stores/projects/project; `null` when there is no active project).
 */
type ProjectSaveState = 'idle' | 'error' | 'busy' | null;

/**
 * Props for {@link SaveFeedback}.
 *
 * - state: 'busy', 'error' or 'idle' (or null when there is no active project)
 * - save: function to manually trigger a save of the project
 */
interface SaveFeedbackProps {
  state: ProjectSaveState;
  save: (() => Promise<unknown>) | null;
}

/**
 * Show feedback about the state of the project autosave.
 */
const SaveFeedback = ({ state, save }: SaveFeedbackProps) => {
  if (state === 'busy') {
    return <span className="ml-[10px]">Saving...</span>;
  } else if (state === 'error') {
    return (
      <span className="ml-[10px]">
        <div className="group relative inline-block">
          <span className="relative top-[-1px] mr-[5px] inline-block">
            <RSIcon name="FaExclamationTriangle" color="#FFAC2C" size={12} />
          </span>
          <div className="absolute z-[100] hidden min-w-[200px] max-w-[calc(100vw-320px)] rounded border border-grey bg-white p-[5px] leading-[15px] group-hover:block">
            We could not save your changes. We will try again automatically.
          </div>
        </div>
        <a
          className="cursor-pointer underline"
          onClick={() => {
            if (save) save();
          }}
        >
          save now.
        </a>
      </span>
    );
  } else {
    return null;
  }
};

/**
 * Render the project title and information about when it was last saved.
 *
 * The values that used to be injected (title, lastSaved, saveState, save) are
 * derived here from the active project, looked up by the `:id` route param
 * (useParams) against the projects store (useStores). This replaces the old
 * `withRouter(inject(...))` wrapper that read `props.match.params.id`.
 */
const ProjectTitle = observer(function ProjectTitle() {
  const store = useStores();
  const { id } = useParams();

  const project = store.projects.getProjectById(id as string);
  const title = project ? project.displayName : '';
  const lastSaved = project ? project.lastEdited : null;
  const saveState = project ? project.saveState : null;
  const save = project ? project.retrySave : null;

  return (
    <React.Fragment>
      {title}
      <div className="-mt-2 text-[12px] text-grey2">
        {lastSaved && (
          <span className="last-saved">
            Last saved <TimeAgoTicker ts={lastSaved.toISO()!} />.
          </span>
        )}
        <SaveFeedback state={saveState} save={save} />
      </div>
    </React.Fragment>
  );
});

export default ProjectTitle;
